import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type ApkRow, type VariantExperiment } from "../lib/api";
import { BuildAge } from "../components/BuildAge";
import { fmtAgo, fmtDateTime } from "../lib/format";

const fmtBytes = (n: number) => (n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
const ACTIVE = new Set(["declared", "built", "collecting"]);
/** Registry vocabulary → user words (page only; the registry keeps its own terms). */
const STATUS_LABEL: Record<string, string> = { declared: "planned", built: "ready to try", collecting: "collecting evidence", concluded: "decided" };

/** Newest build per (experiment, option) — rows arrive newest-first. */
const buildFor = (rows: ApkRow[], exp: string, variant: string) => rows.find((r) => r.variant_exp === exp && r.variant_id === variant);

function Qr({ url }: { url: string }) {
  const [big, setBig] = useState(false);
  const src = `/api/connect-info/qr?url=${encodeURIComponent(url)}`;
  return (
    <>
      <img className="qr qr-small" src={src} alt="scan to install" title={url} onClick={() => setBig(true)} />
      {big && (
        <div className="lightbox" onClick={() => setBig(false)}>
          <img className="qr" style={{ width: "min(80vw, 60vh)", height: "min(80vw, 60vh)" }} src={src} alt="scan to install" />
        </div>
      )}
    </>
  );
}

function CopyCmd({ cmd }: { cmd: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="cmd-row">
      <code className="cmd">{cmd}</code>
      <button
        className="small"
        onClick={() => {
          navigator.clipboard?.writeText(cmd).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? "copied" : "copy"}
      </button>
    </div>
  );
}

function InstallBlock({ build }: { build: ApkRow | undefined }) {
  if (!build) return <div className="muted">not built yet</div>;
  return (
    <div className="row" style={{ alignItems: "center", gap: 10, marginTop: 8 }}>
      <a className="btn primary" href={build.url}>
        install
      </a>
      <Qr url={build.url} />
    </div>
  );
}

/** One decision of kind=branch: a tile per option, each installable side by side. */
export function BranchExperiment({ e, rows }: { e: VariantExperiment; rows: ApkRow[] }) {
  return (
    <>
      {/*
       * ABOVE the tiles, not below them. This line already existed and sat under the install
       * buttons it governs, so the CEO read the pair as alternatives and asked "should i wait?" —
       * the question of someone who believes taking one forecloses the other. A caveat placed after
       * the choice is read after the choice, if at all.
       *
       * It also has to say what IS exclusive, because "install both" alone invites the next wrong
       * inference — that they can run at once. The real constraint is the enabled service, which is
       * a switch rather than an install conflict, and naming the switch is what makes coexistence
       * credible instead of surprising.
       *
       * Deliberately no "recommended" and no default chip on this kind: that would reintroduce the
       * choice framing the line exists to remove. The sibling ToggleExperiment marks a default
       * because there the options genuinely ARE one build's setting.
       */}
      <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
        <strong>Install both — they do not conflict.</strong> Each is a separate app, so taking one
        does not rule out the other. Only one can be the ACTIVE controller at a time: switch between
        them in Android Settings → Accessibility, without uninstalling anything.
      </div>
      <div className="row" style={{ gap: 16, alignItems: "stretch", flexWrap: "wrap" }}>
        {e.options.map((o) => {
          const b = buildFor(rows, e.id, o.variant);
          return (
            <div key={o.variant} className="tile opt-tile">
              <div className="opt-name">{o.variant}</div>
              {o.summary && <div className="opt-summary">{o.summary}</div>}
              <div className="muted mono-line">
                <code>{b?.commit_sha ?? o.tip ?? "?"}</code>
                {b ? ` · ${b.sha256.slice(0, 8)}` : ""}
              </div>
              {(b?.application_id ?? null) && <div className="muted mono-line">{b!.application_id}</div>}
              {b && (
                <div className="muted mono-line">
                  <BuildAge builtTs={b.built_ts} uploadedTs={b.uploaded_ts} /> · {fmtBytes(b.bytes)}
                </div>
              )}
              <InstallBlock build={b} />
            </div>
          );
        })}
      </div>
    </>
  );
}

/** One decision of kind=toggle: the baseline build installs once; options are a runtime switch. */
function ToggleExperiment({ e, rows }: { e: VariantExperiment; rows: ApkRow[] }) {
  const baseline = rows.find((r) => !r.variant_exp && (!e.baseline || r.branch === e.baseline)) ?? rows.find((r) => !r.variant_exp);
  return (
    <>
      <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
        One build (the baseline{e.baseline ? <> · <code>{e.baseline}</code></> : null}). Options are a switch — no reinstall.
      </div>
      <div className="row" style={{ gap: 16, alignItems: "stretch", flexWrap: "wrap" }}>
        {e.options.map((o) => (
          <div key={o.variant} className="tile opt-tile">
            <div className="opt-name">
              {o.variant}
              {o.default && <span className="chip" style={{ marginLeft: 8 }}>default</span>}
            </div>
            {o.summary && <div className="opt-summary">{o.summary}</div>}
            {o.switch && <CopyCmd cmd={o.switch} />}
          </div>
        ))}
      </div>
      {e.toggle?.note && <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>{e.toggle.note}</div>}
      {baseline && (
        <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
          Baseline build: <code>{baseline.commit_sha}</code>
          {baseline.branch && baseline.branch !== e.baseline ? (
            <>
              {" "}on <code>{baseline.branch}</code>
            </>
          ) : null}{" "}
          · <a href={baseline.url}>install</a>
        </div>
      )}
    </>
  );
}

function Experiment({ e, rows }: { e: VariantExperiment; rows: ApkRow[] }) {
  return (
    <div className="panel" key={e.id}>
      <h3 style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <code>{e.id}</code>
        <span className={`req-chip exp-${e.status ?? "declared"}`}>
          {STATUS_LABEL[e.status ?? "declared"] ?? e.status}
          {e.kind === "toggle" ? " (switch)" : ""}
        </span>
        <span style={{ flex: 1 }} />
        {e.board_card != null && (
          <a className="muted" style={{ fontSize: 12 }} href="/board" title={`board card #${e.board_card}`}>
            ⓘ card #{e.board_card}
          </a>
        )}
      </h3>
      <div className="exp-question">{e.question}</div>
      {e.kind === "toggle" ? <ToggleExperiment e={e} rows={rows} /> : <BranchExperiment e={e} rows={rows} />}
    </div>
  );
}

export function Apks() {
  const apks = useQuery({ queryKey: ["apks"], queryFn: api.apks, refetchInterval: 15_000 });
  const variants = useQuery({ queryKey: ["variants"], queryFn: api.variants, refetchInterval: 60_000 });
  const publishers = useQuery({ queryKey: ["publishers"], queryFn: api.publishers });
  const rows = apks.data ?? [];
  const registry = variants.data?.registry ?? null;
  const experiments = registry?.experiments ?? [];

  const active = experiments.filter((e) => ACTIVE.has(e.status ?? "declared"));
  const concluded = experiments.filter((e) => !ACTIVE.has(e.status ?? "declared"));
  // Uploads whose experiment the registry doesn't know — never hide builds.
  const registryIds = new Set(experiments.map((e) => e.id));
  const orphanExps = [...new Set(rows.filter((r) => r.variant_exp && !registryIds.has(r.variant_exp)).map((r) => r.variant_exp!))];
  const baseline = rows.find((r) => !r.variant_exp);

  return (
    <div className="page">
      <div className="page-head">
        <h2>Releases</h2>
        <span className="muted">
          decisions with their options installable side by side ·{" "}
          {publishers.data?.length ? `publishers: ${publishers.data.join(", ")}` : "no publishers configured — uploads disabled"}
        </span>
      </div>

      {active.map((e) => (
        <Experiment key={e.id} e={e} rows={rows} />
      ))}

      {orphanExps.map((exp) => (
        <div className="panel" key={exp}>
          <h3>
            <code>{exp}</code> <span className="muted">· not in the registry — showing upload metadata only</span>
          </h3>
          <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
            {[...new Set(rows.filter((r) => r.variant_exp === exp).map((r) => r.variant_id ?? "?"))].map((vid) => {
              const b = buildFor(rows, exp, vid);
              return (
                <div key={vid} className="tile opt-tile">
                  <div className="opt-name">{vid}</div>
                  <div className="muted mono-line">
                    <code>{b?.commit_sha ?? "?"}</code> {b?.application_id ?? ""}
                  </div>
                  <InstallBlock build={b} />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {baseline && (
        <div className="panel">
          <h3>
            Baseline · <code>{baseline.branch ?? "?"}</code>
          </h3>
          <div className="row" style={{ alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="muted">
                {[baseline.commit_sha ?? "no commit", baseline.built_ts ? `built ${fmtAgo(baseline.built_ts)}` : `published ${fmtAgo(baseline.uploaded_ts)}`, baseline.application_id, fmtBytes(baseline.bytes)]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              <div className="row" style={{ alignItems: "center", gap: 10, marginTop: 8 }}>
                <a className="btn primary" href={baseline.url}>
                  install
                </a>
                <Qr url={baseline.url} />
              </div>
            </div>
          </div>
        </div>
      )}

      {concluded.length > 0 && (
        <details className="panel">
          <summary style={{ cursor: "pointer" }}>
            Concluded ({concluded.length})
          </summary>
          {concluded.map((e) => (
            <div key={e.id} style={{ marginTop: 10 }}>
              <div>
                <code>{e.id}</code>{" "}
                <span className="muted">
                  {e.decision ? `— ${e.decision}` : "— concluded"}
                </span>
              </div>
              <Experiment e={e} rows={rows} />
            </div>
          ))}
        </details>
      )}

      <details className="panel">
        <summary style={{ cursor: "pointer" }}>All builds ({rows.length})</summary>
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table className="table">
            <thead>
              <tr>
                <th>uploaded</th>
                <th>version</th>
                <th>variant</th>
                <th>commit</th>
                <th>size</th>
                <th>publisher</th>
                <th>sha256</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><BuildAge builtTs={r.built_ts} uploadedTs={r.uploaded_ts} /></td>
                  <td>
                    {r.version_name ?? "?"} <span className="muted">({r.version_code ?? "?"})</span>
                  </td>
                  <td>
                    {r.variant}
                    {r.variant_exp && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {r.variant_exp}/{r.variant_id ?? "?"}
                      </div>
                    )}
                  </td>
                  <td>
                    <code>{r.commit_sha ?? ""}</code> <span className="muted">{r.branch ?? ""}</span>
                  </td>
                  <td>{fmtBytes(r.bytes)}</td>
                  <td>{r.publisher ?? "—"}</td>
                  <td>
                    <code title={r.sha256}>{r.sha256.slice(0, 12)}</code>
                  </td>
                  <td>
                    <a className="btn small" href={r.url}>
                      download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
